'use client';

import { useCallback, useEffect, useRef } from 'react';
import { FetchWithVersionCheck } from '@/shared/functions';

interface IdentityContext {
  userId?: string;
  email?: string;
  userName?: string;
  tenantId?: string;
  tenantName?: string;
  orgId?: string;
  systemStage?: string;
}

interface UseIntercomScreenshotOptions {
  enabled: boolean;
  accessToken: string | undefined;
  idToken: string | undefined;
  caseId: string;
  selectedView: string;
  chatSessionId: string;
  identity?: IdentityContext;
}

const SCREENSHOT_BTN_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24">' +
  '<path fill="currentColor" d="M4 4h3l2-2h6l2 2h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/>' +
  '<circle cx="12" cy="12" r="4" fill="white"/></svg>';

const TICKET_FORM_SELECTOR = 'form';

const CUSTOM_FIELDS = [
  { id: 'what-happened', label: 'What happened?', placeholder: 'Briefly describe the issue you encountered', required: true },
  { id: 'expected-behavior', label: 'What is the expected behavior?', placeholder: 'How do you want it to work?', required: true },
  { id: 'steps-to-reproduce', label: 'Steps to reproduce', subtitle: 'Please include the file path if applicable', placeholder: '1. Go to...\n2. Click on...\n3. See error', required: true },
] as const;

const LABEL_CSS = 'display:block;font-size:14px;font-weight:600;color:#1a1a1a;margin-bottom:4px;';
const SUBTITLE_CSS = 'display:inline-block;font-size:12px;font-weight:500;color:#92600a;background:#fef9e7;padding:2px 8px;border-radius:4px;margin-bottom:6px;';
const TEXTAREA_CSS = 'width:100%;min-height:80px;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px;font-family:inherit;resize:vertical;box-sizing:border-box;color:#1a1a1a;background:#fff;outline:none;transition:border-color 0.15s;';
const TEXTAREA_FOCUS_BORDER = '#1972f5';
const REQUIRED_CSS = 'color:#e53e3e;margin-left:2px;';
const ERROR_CSS = 'font-size:12px;color:#e53e3e;margin-top:4px;display:none;';
const PLACEHOLDER_STYLE_TAG_ID = 'intercom-custom-placeholder-styles';

export function useIntercomTicketForm({
  enabled,
  accessToken,
  idToken,
  caseId,
  selectedView,
  chatSessionId,
  identity,
}: UseIntercomScreenshotOptions) {
  const inProgressRef = useRef(false);
  const screenshotBase64Ref = useRef<string | null>(null);
  const bypassRef = useRef(false);

  const captureScreenshot = useCallback(async () => {
    if (inProgressRef.current) return;
    inProgressRef.current = true;

    const intercomFrame = document.querySelector('iframe[name="intercom-messenger-frame"]')?.parentElement?.parentElement as HTMLElement | null;
    const intercomLauncher = document.querySelector('iframe[name="intercom-launcher-frame"]')?.parentElement as HTMLElement | null;
    if (intercomFrame) intercomFrame.style.display = 'none';
    if (intercomLauncher) intercomLauncher.style.display = 'none';
    await new Promise(r => setTimeout(r, 300));

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' } as MediaTrackConstraints,
        preferCurrentTab: true,
      } as DisplayMediaStreamOptions);

      const track = stream.getVideoTracks()[0];
      const imageCapture = new ((window as unknown as Record<string, unknown>).ImageCapture as new (track: MediaStreamTrack) => { grabFrame(): Promise<ImageBitmap> })(track);
      const bitmap: ImageBitmap = await imageCapture.grabFrame();
      stream.getTracks().forEach(t => t.stop());

      const MAX_DIM = 1280;
      const MAX_BYTES = 280_000;
      let w = bitmap.width;
      let h = bitmap.height;
      if (w > MAX_DIM || h > MAX_DIM) {
        const scale = MAX_DIM / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0, w, h);

      let quality = 0.7;
      let dataUrl = canvas.toDataURL('image/webp', quality);
      while (dataUrl.length > MAX_BYTES && quality > 0.15) {
        quality -= 0.1;
        dataUrl = canvas.toDataURL('image/webp', quality);
      }
      screenshotBase64Ref.current = dataUrl;
    } catch {
      // Silent failure — screenshot is best-effort
    } finally {
      if (intercomFrame) intercomFrame.style.display = '';
      if (intercomLauncher) intercomLauncher.style.display = '';
      setTimeout(() => { inProgressRef.current = false; }, 1000);
    }
  }, []);

  const getCustomFieldValues = (doc: Document) => {
    const values: Record<string, string> = {};
    for (const field of CUSTOM_FIELDS) {
      const textarea = doc.querySelector(`[data-custom-field="${field.id}"]`) as HTMLTextAreaElement | null;
      values[field.id] = textarea?.value?.trim() ?? '';
    }
    return values;
  };

  const validateCustomFields = (doc: Document): boolean => {
    const values = getCustomFieldValues(doc);
    let valid = true;

    for (const field of CUSTOM_FIELDS) {
      if (!field.required) continue;
      const textarea = doc.querySelector(`[data-custom-field="${field.id}"]`) as HTMLTextAreaElement | null;
      const errorEl = doc.querySelector(`[data-field-error="${field.id}"]`) as HTMLElement | null;

      if (!values[field.id]) {
        if (textarea) textarea.style.borderColor = '#e53e3e';
        if (errorEl) { errorEl.style.display = 'block'; errorEl.textContent = 'This field is required'; }
        valid = false;
      } else {
        if (textarea) textarea.style.borderColor = '#e0e0e0';
        if (errorEl) errorEl.style.display = 'none';
      }
    }
    return valid;
  };

  const handleTicketSubmit = useCallback(async () => {
    if (!accessToken || !idToken) {
      bypassRef.current = true;
      const f = document.querySelector('iframe[name="intercom-messenger-frame"]') as HTMLIFrameElement | null;
      const btn = f?.contentDocument?.querySelector('button[aria-label="Create ticket"]') as HTMLButtonElement | null;
      btn?.click();
      return;
    }

    const iframe = document.querySelector('iframe[name="intercom-messenger-frame"]') as HTMLIFrameElement | null;
    const doc = iframe?.contentDocument;
    if (!doc) return;

    const header = doc.querySelector('h1');
    const isIssueForm = header?.textContent?.trim() === 'Issue';

    // Validate custom fields only on Issue form
    if (isIssueForm && !validateCustomFields(doc)) return;

    const form = doc.querySelector('form');
    const formFields: Record<string, string> = {};
    if (form) {
      form.querySelectorAll('input, textarea').forEach((el) => {
        const field = el as HTMLInputElement | HTMLTextAreaElement;
        const label = field.getAttribute('aria-label');
        if (label && field.value) {
          formFields[label] = field.value;
        }
      });
    }

    const customValues = isIssueForm ? getCustomFieldValues(doc) : null;
    if (customValues) {
      formFields['What happened?'] = customValues['what-happened'];
      formFields['Expected behavior'] = customValues['expected-behavior'];
      formFields['Steps to reproduce'] = customValues['steps-to-reproduce'];
    }

    const intercomEventData: Record<string, unknown> = {
      formFields,
      tabContext: {
        url: window.location.href,
        pathname: window.location.pathname,
        pageTitle: document.title,
        caseId: caseId ?? '',
        selectedView: selectedView ?? '',
        chatSessionId: chatSessionId ?? '',
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        viewportSize: `${window.innerWidth}x${window.innerHeight}`,
        devicePixelRatio: window.devicePixelRatio,
        language: navigator.language,
        referrer: document.referrer || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timeOnPageSeconds: Math.round(performance.now() / 1000),
        timestamp: new Date().toISOString(),
      },
      identity: {
        userId: identity?.userId ?? '',
        email: identity?.email ?? '',
        userName: identity?.userName ?? '',
        tenantId: identity?.tenantId ?? '',
        tenantName: identity?.tenantName ?? '',
        orgId: identity?.orgId ?? '',
        systemStage: identity?.systemStage ?? '',
      },
    };

    if (screenshotBase64Ref.current) {
      intercomEventData.screenshot = screenshotBase64Ref.current;
    }

    let confirmedEventKey: string | undefined;
    try {
      const res = await FetchWithVersionCheck('/api/protected/intercom', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': idToken,
        },
        body: JSON.stringify({
          event_name: 'ticket_submission',
          intercomEventData,
        }),
      });
      const data = await res.json().catch(() => null);
      if (data?.error) console.error('Intercom ticket event error:', data.error);
      confirmedEventKey = data?.event_key;
    } catch (err) {
      console.error('Intercom ticket event failed:', err);
    }

    const descTextarea = doc.querySelector('textarea[aria-label="Description"]') as HTMLTextAreaElement | null;
    if (customValues && descTextarea) {
      const parts = [
        `What happened?\n${customValues['what-happened']}`,
        `Expected behavior:\n${customValues['expected-behavior']}`,
        `Steps to reproduce:\n${customValues['steps-to-reproduce']}`,
      ];
      if (confirmedEventKey) parts.push(`[internal_key=${confirmedEventKey}]`);
      descTextarea.focus();
      descTextarea.setSelectionRange(0, descTextarea.value.length);
      doc.execCommand('insertText', false, parts.join('\n\n'));
    } else if (confirmedEventKey && descTextarea) {
      descTextarea.focus();
      descTextarea.setSelectionRange(descTextarea.value.length, descTextarea.value.length);
      doc.execCommand('insertText', false, `\n\n[internal_key=${confirmedEventKey}]`);
    }

    screenshotBase64Ref.current = null;
    bypassRef.current = true;
    const submitBtn = doc.querySelector('button[aria-label="Create ticket"]') as HTMLButtonElement | null;
    submitBtn?.click();
  }, [accessToken, idToken, caseId, selectedView, chatSessionId]);

  useEffect(() => {
    if (!enabled) return;

    const poll = () => {
      const iframe = document.querySelector('iframe[name="intercom-messenger-frame"]') as HTMLIFrameElement | null;
      if (!iframe) return;

      let doc: Document | null = null;
      try { doc = iframe.contentDocument; } catch { return; }
      if (!doc?.body) return;

      const form = doc.querySelector(TICKET_FORM_SELECTOR);
      if (!form) return;

      const titleInput = form.querySelector('input[aria-label="Title"]');
      if (!titleInput) return;
      const titleFieldDiv = titleInput.closest('div:has(> label)') ?? titleInput.parentElement;
      if (!titleFieldDiv) return;

      // Detect if this is the "Issue" form
      const header = doc.querySelector('h1');
      const isIssueForm = header?.textContent?.trim() === 'Issue';

      if (isIssueForm) {
        // Hide native Description field (keep focusable so execCommand works)
        const descTextarea = form.querySelector('textarea[aria-label="Description"]');
        if (descTextarea) {
          const descDiv = descTextarea.closest('div:has(> label)') ?? descTextarea.parentElement;
          if (descDiv) (descDiv as HTMLElement).style.cssText = 'position:absolute;opacity:0;height:0;overflow:hidden;pointer-events:none;';
        }
      } else {
        // Non-Issue form: remove custom fields and restore Description if leftover from previous form
        const customFields = doc.querySelector('[data-custom-fields]');
        if (customFields) customFields.remove();
        const placeholderStyles = doc.getElementById(PLACEHOLDER_STYLE_TAG_ID);
        if (placeholderStyles) placeholderStyles.remove();
        const descTextarea = form.querySelector('textarea[aria-label="Description"]');
        if (descTextarea) {
          const descDiv = descTextarea.closest('div:has(> label)') ?? descTextarea.parentElement;
          if (descDiv) (descDiv as HTMLElement).style.cssText = '';
        }
      }

      // Inject screenshot button (after Title)
      if (!doc.querySelector('[data-screenshot-btn]')) {
        const btn = doc.createElement('button');
        btn.setAttribute('data-screenshot-btn', 'true');
        btn.setAttribute('tabindex', '0');
        btn.setAttribute('type', 'button');
        btn.style.cssText = 'display:inline-flex;align-items:center;gap:6px;padding:6px 12px;margin-top:8px;margin-bottom:8px;border:1px solid #ccc;border-radius:6px;background:#f7f7f7;color:#333;cursor:pointer;font-size:13px;';

        const span = doc.createElement('span');
        span.className = 'ss-label';
        span.style.cssText = 'display:flex;align-items:center;gap:5px;';
        span.innerHTML = `${SCREENSHOT_BTN_SVG} Submit Screenshot`;
        btn.appendChild(span);

        btn.addEventListener('click', (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          window.postMessage({ type: 'intercom-screenshot-request' }, '*');
        });

        titleFieldDiv.parentElement!.insertBefore(btn, titleFieldDiv.nextSibling);
      } else {
        const label = doc.querySelector('[data-screenshot-btn] .ss-label');
        if (label && screenshotBase64Ref.current && !label.textContent?.includes('\u2713')) {
          label.textContent = '\u2713 Screenshot captured';
        }
      }

      if (isIssueForm) {
        // Inject placeholder styles into iframe document
        if (!doc.getElementById(PLACEHOLDER_STYLE_TAG_ID)) {
          const style = doc.createElement('style');
          style.id = PLACEHOLDER_STYLE_TAG_ID;
          style.textContent = '[data-custom-field]::placeholder { color: #b0b0b0; opacity: 1; }';
          doc.head.appendChild(style);
        }

        // Inject custom fields (after screenshot button)
        if (!doc.querySelector('[data-custom-fields]')) {
          const container = doc.createElement('div');
          container.setAttribute('data-custom-fields', 'true');

          for (const field of CUSTOM_FIELDS) {
            const wrapper = doc.createElement('div');
            wrapper.style.cssText = 'margin-bottom:12px;';

            const lbl = doc.createElement('label');
            lbl.style.cssText = LABEL_CSS;
            lbl.textContent = field.label;
            if (field.required) {
              const asterisk = doc.createElement('span');
              asterisk.style.cssText = REQUIRED_CSS;
              asterisk.textContent = '*';
              lbl.appendChild(asterisk);
            }
            wrapper.appendChild(lbl);

            if ('subtitle' in field && field.subtitle) {
              const sub = doc.createElement('span');
              sub.style.cssText = SUBTITLE_CSS;
              sub.textContent = field.subtitle;
              wrapper.appendChild(sub);
            }

            const textarea = doc.createElement('textarea');
            textarea.setAttribute('data-custom-field', field.id);
            textarea.setAttribute('placeholder', field.placeholder);
            textarea.style.cssText = TEXTAREA_CSS;
            textarea.rows = 3;
            textarea.addEventListener('focus', () => { textarea.style.borderColor = TEXTAREA_FOCUS_BORDER; });
            textarea.addEventListener('blur', () => { textarea.style.borderColor = '#e0e0e0'; });
            wrapper.appendChild(textarea);

            const errorMsg = doc.createElement('div');
            errorMsg.setAttribute('data-field-error', field.id);
            errorMsg.style.cssText = ERROR_CSS;
            wrapper.appendChild(errorMsg);

            container.appendChild(wrapper);
          }

          const screenshotBtn = doc.querySelector('[data-screenshot-btn]');
          if (screenshotBtn) {
            screenshotBtn.parentElement!.insertBefore(container, screenshotBtn.nextSibling);
          }
        }

      }

      // Intercept submit for ALL ticket forms (screenshot + event tracking)
      const submitBtn = doc.querySelector('button[aria-label="Create ticket"]') as HTMLButtonElement | null;
      if (submitBtn && !submitBtn.hasAttribute('data-submit-intercepted')) {
        submitBtn.setAttribute('data-submit-intercepted', 'true');
        submitBtn.addEventListener('click', (e: Event) => {
          if (bypassRef.current) {
            bypassRef.current = false;
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          window.postMessage({ type: 'intercom-ticket-submit' }, '*');
        }, { capture: true });
      }
    };

    const interval = setInterval(poll, 500);
    poll();

    return () => clearInterval(interval);
  }, [enabled]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'intercom-screenshot-request') {
        captureScreenshot();
      } else if (e.data?.type === 'intercom-ticket-submit') {
        handleTicketSubmit();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [captureScreenshot, handleTicketSubmit]);
}
